/**
 * 締切時間インジケーター
 * 現在の締切時間と受付状態を表示
 */
(function() {
  'use strict';

  class DeadlineIndicator {
    constructor() {
      this.businessDayCalculator = window.businessDayCalculator;
      this.containerId = 'deadline-indicator-container';
    }

    /**
     * 初期化
     */
    async init() {
      // BusinessDayCalculatorの読み込みを待つ
      if (!this.businessDayCalculator) {
        // 最大5秒待つ
        let retries = 0;
        const checkInterval = setInterval(async () => {
          if (window.businessDayCalculator || retries > 50) {
            clearInterval(checkInterval);
            this.businessDayCalculator = window.businessDayCalculator;
            if (this.businessDayCalculator) {
              // 休日データの読み込みを確実に待つ
              await this.businessDayCalculator.loadHolidays();
              this.render();
              this.startAutoUpdate();
            }
          }
          retries++;
        }, 100);
      } else {
        // 休日データの読み込みを確実に待つ
        await this.businessDayCalculator.loadHolidays();
        this.render();
        this.startAutoUpdate();
      }
    }

    /**
     * 締切時間を取得
     * 優先順位: 1. 商品メタフィールド 2. Shop Metafield
     */
    getCutoffTime() {
      // 1. 商品個別設定を確認
      const productConfig = window.productCutoffConfig;

      if (productConfig) {
        if (productConfig.mode === 'hidden') {
          return null; // 非表示
        }
        if (productConfig.mode === 'custom' && productConfig.time) {
          return productConfig.time; // 個別設定
        }
      }

      // 2. デフォルト（営業日カレンダー設定）
      return this.businessDayCalculator?.settings?.cutoffTime || '12:00';
    }

    /**
     * 締切時間超過チェック
     */
    isPastCutoff() {
      if (!this.businessDayCalculator) return false;
      return this.businessDayCalculator.isPastCutoffTime(new Date());
    }

    /**
     * 次の営業日を取得
     */
    getNextBusinessDay() {
      if (!this.businessDayCalculator || !this.businessDayCalculator.loaded) {
        return '翌営業日';
      }
      
      try {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        
        // BusinessDayCalculatorのaddBusinessDaysメソッドを使用
        const nextBusinessDay = this.businessDayCalculator.addBusinessDays(new Date(), 1);
        
        const formatter = new Intl.DateTimeFormat('ja-JP', {
          month: 'numeric',
          day: 'numeric',
          weekday: 'short'
        });
        
        return formatter.format(nextBusinessDay);
      } catch (error) {
        console.warn('[DeadlineIndicator] Error calculating next business day:', error);
        return '翌営業日';
      }
    }

    /**
     * インジケーターをレンダリング
     */
    render() {
      const container = document.getElementById(this.containerId);
      if (!container) {
        console.warn('[DeadlineIndicator] Container not found:', this.containerId);
        return;
      }

      const cutoffTime = this.getCutoffTime();

      // 締切時間が null の場合は非表示
      if (cutoffTime === null) {
        container.innerHTML = '';
        return;
      }

      const isPastCutoff = this.isPastCutoff();
      const nextBusinessDay = this.getNextBusinessDay();

      const html = `
        <div class="deadline-indicator ${isPastCutoff ? 'past-deadline' : 'before-deadline'}">
          <div class="deadline-content">
            <span class="deadline-icon">${isPastCutoff ? '🔴' : '🟢'}</span>
            <span class="deadline-text">
              この商品の締切時間: <strong>${cutoffTime}</strong>
              ${isPastCutoff
                ? `<span class="deadline-status past">（締切済み・${nextBusinessDay}受付）</span>`
                : `<span class="deadline-status active">（受付中）</span>`
              }
            </span>
          </div>
          <div class="deadline-note">
            ${isPastCutoff
              ? `※ 本日${cutoffTime}を過ぎたため、ご注文は${nextBusinessDay}の受付となります`
              : `※ ${cutoffTime}以降のご注文は翌日以降の受付となります`
            }
          </div>
        </div>
      `;

      container.innerHTML = html;
    }

    /**
     * 自動更新を開始（1分ごとにチェック）
     */
    startAutoUpdate() {
      // 初回チェック
      let lastStatus = this.isPastCutoff();

      setInterval(() => {
        const currentStatus = this.isPastCutoff();
        
        // ステータスが変わった場合は再描画
        if (currentStatus !== lastStatus) {
          this.render();
          lastStatus = currentStatus;
          
          // ページ全体の価格計算も更新
          if (window.priceCalculator) {
            window.priceCalculator.updateUI();
          }
        }
      }, 60000); // 1分ごと
    }

    /**
     * スタイルを挿入
     */
    injectStyles() {
      if (document.getElementById('deadline-indicator-styles')) return;

      const styles = `
        <style id="deadline-indicator-styles">
          .deadline-indicator {
            padding: 12px 16px;
            margin: 10px 0;
            border-radius: 8px;
            background: #f0f8ff;
            border: 1px solid #d0e0f0;
            font-size: 14px;
            line-height: 1.5;
          }
          
          .deadline-indicator.past-deadline {
            background: #fff5f5;
            border-color: #ffd0d0;
          }
          
          .deadline-content {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 4px;
          }
          
          .deadline-icon {
            font-size: 12px;
          }
          
          .deadline-text strong {
            color: #333;
            font-weight: 600;
          }
          
          .deadline-status {
            margin-left: 8px;
            font-weight: 500;
          }
          
          .deadline-status.active {
            color: #22a06b;
          }
          
          .deadline-status.past {
            color: #e34935;
          }
          
          .deadline-note {
            margin-left: 24px;
            font-size: 12px;
            color: #666;
          }
          
          /* モバイル対応 */
          @media (max-width: 768px) {
            .deadline-indicator {
              font-size: 13px;
              padding: 10px 12px;
            }
            
            .deadline-content {
              flex-wrap: wrap;
            }
            
            .deadline-note {
              margin-left: 0;
              margin-top: 4px;
            }
          }
        </style>
      `;

      document.head.insertAdjacentHTML('beforeend', styles);
    }
  }

  // グローバルに公開
  window.DeadlineIndicator = DeadlineIndicator;

  // DOM読み込み完了時に自動初期化（コンテナが存在する場合）
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const indicator = new DeadlineIndicator();
      indicator.injectStyles();
      if (document.getElementById('deadline-indicator-container')) {
        indicator.init();
      }
    });
  } else {
    const indicator = new DeadlineIndicator();
    indicator.injectStyles();
    if (document.getElementById('deadline-indicator-container')) {
      indicator.init();
    }
  }

})();